import React, { useEffect, useState, useCallback } from 'react';
import { getModeStatus, promoteMode } from '../lib/api';
import { Activity, ShieldAlert, ShieldCheck, Lock, Unlock, AlertCircle, RefreshCw, ChevronRight, Power, Pause, Eye } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import logger from '../lib/logger';

// Mode Promotion ladder (Slice 3 — May 7 2026):
//   SHADOW → MONITOR → ACTIVE
// Per-outlet, audited, gated. Operator must confirm consequences.

const MODE_META = {
  shadow: {
    label: 'SHADOW',
    icon: Eye,
    desc: 'Fetch + map + classify; ZERO writes.',
    tone: 'bg-zinc-500/10 text-zinc-300 border-zinc-500/30',
    pillTone: 'bg-zinc-600 text-zinc-100',
  },
  monitor: {
    label: 'MONITOR',
    icon: Pause,
    desc: 'Depletion math runs; NO inventory mutations. Trust validation phase.',
    tone: 'bg-blue-500/10 text-blue-300 border-blue-500/30',
    pillTone: 'bg-blue-600 text-blue-50',
  },
  active: {
    label: 'ACTIVE',
    icon: Power,
    desc: 'Trusted autonomous depletion writes to ledger.',
    tone: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
    pillTone: 'bg-emerald-600 text-emerald-50',
  },
};

const ModePill = ({ mode, current }) => {
  const m = MODE_META[mode];
  if (!m) return null;
  const isCurrent = mode === current;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-bold uppercase tracking-wider',
        isCurrent ? m.pillTone : 'bg-zinc-800 text-zinc-500'
      )}
      data-testid={`mode-pill-${mode}`}
    >
      <m.icon className="w-3 h-3" />
      {m.label}
    </span>
  );
};

const ConfirmModal = ({ payload, onClose, onConfirm, loading }) => {
  if (!payload) return null;
  const m = MODE_META[payload.target_mode];
  const isHigh = payload.consequences?.impact === 'high_risk';
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="mode-promotion-confirm-modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#0a0a0a] border border-[#27272a] rounded-xl max-w-lg w-full">
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            {isHigh ? <ShieldAlert className="w-5 h-5 text-red-400" /> : <ShieldCheck className="w-5 h-5 text-emerald-400" />}
            <h3 className="text-lg font-semibold">
              {payload.consequences?.headline || `Promote to ${m?.label}`}
            </h3>
          </div>
          <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-3 space-y-1.5 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Coverage</span>
              <span className="font-mono font-bold text-zinc-100">{(payload.consequences?.coverage_pct || 0).toFixed(1)}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-400">Readiness</span>
              <span className="font-mono font-bold text-zinc-100">{(payload.consequences?.readiness_pct || 0).toFixed(1)}%</span>
            </div>
          </div>
          <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium">This will:</p>
          <ul className="space-y-1.5 text-sm">
            {(payload.consequences?.consequences || []).map((c, i) => (
              <li key={i} className="flex items-start gap-2">
                <ChevronRight className={cn('w-4 h-4 flex-shrink-0 mt-0.5', isHigh ? 'text-red-400' : 'text-emerald-400')} />
                <span className="text-zinc-300">{c}</span>
              </li>
            ))}
          </ul>
          <div>
            <label className="text-xs text-zinc-400 block mb-1">Reason / context (recorded in audit log)</label>
            <input
              type="text"
              value={payload.reason || ''}
              onChange={(e) => payload.onReasonChange?.(e.target.value)}
              placeholder="e.g. recipes verified manually; ready to ship"
              className="w-full px-3 py-2 bg-[#18181b] border border-[#27272a] rounded text-sm focus:outline-none focus:border-zinc-500"
              data-testid="mode-promotion-reason-input"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 bg-[#27272a] hover:bg-[#3f3f46] rounded-md text-sm font-medium disabled:opacity-50"
              data-testid="mode-promotion-cancel"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className={cn(
                'px-4 py-2 rounded-md text-sm font-bold text-white disabled:opacity-50',
                isHigh ? 'bg-red-500 hover:bg-red-600' : 'bg-emerald-500 hover:bg-emerald-600'
              )}
              data-testid="mode-promotion-confirm"
            >
              {loading ? <RefreshCw className="w-3.5 h-3.5 mr-1 inline animate-spin" /> : null}
              Confirm — {m?.label}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ModePromotionPanel = ({ outletId, refreshKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmPayload, setConfirmPayload] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    if (!outletId) return;
    setLoading(true);
    try {
      const res = await getModeStatus(outletId);
      setData(res);
    } catch (e) {
      logger.warn('[ModePromotionPanel] fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, [outletId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const buildConsequencesPayload = (target_mode) => {
    const r = data?.readiness_snapshot || {};
    const m = MODE_META[target_mode];
    const isHigh = target_mode === 'active';
    const isDemotion = (data?.current_mode === 'active' && target_mode !== 'active') || (data?.current_mode === 'monitor' && target_mode === 'shadow');
    const consequences = isHigh
      ? [
          'Depletion ledger entries will be written automatically on every sync.',
          'Mapped sales will reduce stock-on-hand.',
          'Procurement planning will start consuming this outlet\'s depletion.',
          'Inventory valuation will reflect autonomous depletion.',
          'This is an OPERATIONAL COMMITMENT — kill-switch back if anything looks wrong.',
        ]
      : isDemotion
      ? [
          'All depletion math will STOP (or downgrade to monitor-only).',
          'Existing historical depletion entries are preserved (no rollback).',
          'Future syncs will respect the new mode immediately.',
        ]
      : [
          'Depletion math will run on every sync.',
          'NO inventory mutations / NO ledger writes / NO stock deduction.',
          'Variance vs audit can now be computed (trust validation phase).',
          'This is the "prove the engine" step — required before ACTIVE.',
        ];
    return {
      target_mode,
      consequences: {
        headline: isDemotion
          ? `Demote to ${m.label}`
          : `Enable ${m.label}`,
        impact: isHigh ? 'high_risk' : isDemotion ? 'demotion' : 'low_risk',
        coverage_pct: r.coverage_pct,
        readiness_pct: r.readiness_pct,
        consequences,
      },
      reason: '',
    };
  };

  const requestPromotion = (target_mode) => {
    setReason('');
    setConfirmPayload(buildConsequencesPayload(target_mode));
  };

  const handleConfirm = async () => {
    if (!confirmPayload) return;
    setConfirming(true);
    try {
      const res = await promoteMode({
        outlet_id: outletId,
        target_mode: confirmPayload.target_mode,
        reason: reason || null,
        confirm_consequences: true,
      });
      toast.success(`Mode changed: ${res.previous_mode.toUpperCase()} → ${res.new_mode.toUpperCase()}`);
      setConfirmPayload(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === 'TRANSITION_FORBIDDEN') {
        toast.error(detail.message);
      } else if (detail?.code === 'ACTIVE_GATE_FAILED') {
        toast.error('ACTIVE gate failed. See readiness panel for blockers.');
      } else if (detail?.code === 'CONFIRMATION_REQUIRED') {
        toast.error('Confirmation required. Click again to confirm.');
      } else {
        toast.error('Promotion failed.');
      }
      logger.error('[ModePromotionPanel] promote error:', e);
    } finally {
      setConfirming(false);
    }
  };

  if (loading || !data) {
    return (
      <div
        className="p-3 bg-[#18181b] border border-[#27272a] rounded-lg text-xs text-zinc-500 flex items-center gap-2"
        data-testid="mode-panel-loading"
      >
        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
        Loading mode...
      </div>
    );
  }

  const current = data.current_mode;
  const promote = data.promote_to || {};
  const audit = data.recent_audit || [];

  return (
    <div className="rounded-xl border border-[#27272a] bg-[#0a0a0a]" data-testid="mode-promotion-panel">
      <div className="px-4 py-3 flex items-center gap-3 border-b border-[#27272a]">
        <Activity className="w-5 h-5 text-zinc-400" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-zinc-100">Sales Capture mode (per outlet)</p>
          <p className="text-xs text-zinc-500 mt-0.5">
            Doctrine: <span className="text-zinc-300 font-mono">SHADOW → MONITOR → ACTIVE</span>. Promotion is staged, gated, audited.
          </p>
        </div>
        <button onClick={load} className="p-1.5 rounded hover:bg-[#27272a]" data-testid="mode-panel-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Ladder */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="mode-ladder">
          {['shadow', 'monitor', 'active'].map((m, i) => (
            <React.Fragment key={m}>
              <ModePill mode={m} current={current} />
              {i < 2 && <ChevronRight className="w-3 h-3 text-zinc-600" />}
            </React.Fragment>
          ))}
          <div className="ml-auto text-[11px] text-zinc-500">
            current: <span className="font-mono text-zinc-300 uppercase">{current}</span>
          </div>
        </div>

        {/* Action buttons — promotion / demotion */}
        <div className="flex flex-wrap gap-2">
          {Object.entries(promote).map(([target, info]) => {
            const m = MODE_META[target];
            const allowed = info.allowed;
            const isUp = (current === 'shadow' && target === 'monitor') || (current === 'monitor' && target === 'active');
            const isHigh = target === 'active';
            return (
              <button
                key={target}
                disabled={!allowed}
                onClick={() => allowed && requestPromotion(target)}
                title={!allowed ? info.reason : undefined}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold border transition-colors',
                  !allowed && 'opacity-40 cursor-not-allowed bg-zinc-900 border-zinc-800 text-zinc-500',
                  allowed && isHigh && 'bg-red-500/10 hover:bg-red-500/20 text-red-300 border-red-500/30',
                  allowed && !isHigh && isUp && 'bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 border-blue-500/30',
                  allowed && !isUp && !isHigh && 'bg-zinc-700/30 hover:bg-zinc-700/50 text-zinc-300 border-zinc-600'
                )}
                data-testid={`promote-to-${target}`}
              >
                {allowed ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                {isUp ? 'Promote' : 'Demote'} to {m?.label}
              </button>
            );
          })}
        </div>

        {/* ACTIVE gate visualization (only relevant when current is monitor) */}
        {data.active_gate && current !== 'active' && (
          <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-3" data-testid="mode-active-gate">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium mb-2 flex items-center gap-1.5">
              <Lock className="w-3 h-3" /> ACTIVE gate
            </p>
            <ul className="space-y-1.5 text-xs">
              {(data.active_gate.checks || []).map((c, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className={cn(
                    'inline-flex w-3.5 h-3.5 items-center justify-center rounded-full flex-shrink-0',
                    c.passed ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                  )}>
                    {c.passed ? '✓' : '✗'}
                  </span>
                  <span className={cn('flex-1', c.passed ? 'text-zinc-300' : 'text-red-300 font-medium')}>{c.label}</span>
                  <span className="font-mono text-zinc-500">
                    {typeof c.value === 'number' ? c.value.toFixed(1) : c.value}
                    {c.code !== 'ZERO_HARD_STRUCTURAL_BLOCKERS' && '%'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Recent audit */}
        {audit.length > 0 && (
          <details className="bg-[#18181b] border border-[#27272a] rounded-lg" data-testid="mode-audit-log">
            <summary className="px-3 py-2 text-xs font-medium text-zinc-300 cursor-pointer">
              Audit log ({audit.length})
            </summary>
            <div className="px-3 pb-3 space-y-1.5 text-[11px]">
              {audit.slice(0, 8).map((a, i) => (
                <div key={i} className="flex items-center gap-2 text-zinc-400">
                  <span className="font-mono text-zinc-500 flex-shrink-0">
                    {a.changed_at?.slice(0, 19).replace('T', ' ')}
                  </span>
                  <span className="font-mono uppercase text-zinc-300">{a.previous_mode}→{a.new_mode}</span>
                  <span className="truncate">{a.changed_by_email || a.changed_by}</span>
                  {a.reason && <span className="text-zinc-500 truncate">· {a.reason}</span>}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      <ConfirmModal
        payload={confirmPayload ? { ...confirmPayload, reason, onReasonChange: setReason } : null}
        onClose={() => setConfirmPayload(null)}
        onConfirm={handleConfirm}
        loading={confirming}
      />
    </div>
  );
};

export default ModePromotionPanel;
